import environment from '../config/environment';
import { creditConfig } from '../constants/credit';
import { CustomError } from '../CustomError';
import { ErrorCodes } from '../constants/credit';
import { getCreditApplicationDetails } from '../apis/credit';
import { messageContents } from '../constants/message';
import { openChat } from './chat';
import { openMessageModal } from '../utils/message';
import { State } from '../types/profile';
import {
  BillingFormModel,
  CompanyFormModel,
  CreditApplicationDetails,
  CreditApplicationStatusResponse,
  CreditApplicationStatusType,
  CreditFormSubVariant,
  CreditState,
  CreditStepKey,
} from '../types/credit';

export const isCompanyFormModel = (data: unknown): data is CompanyFormModel => {
  return (
    data !== null && typeof data === 'object' && 'businessLegalName' in data && 'address' in data
  );
};

export const getActiveStepIndex = (activeStepKey: CreditStepKey | null) => {
  return creditConfig.stepperOrder.findIndex((e) => e === activeStepKey);
};

export const showSubmitButton = (activeStepKey: CreditStepKey | null) => {
  return activeStepKey === creditConfig.stepperOrder[creditConfig.stepperOrder.length - 1];
};

export const showForm = (stepKey: CreditStepKey, activeStepKey: CreditStepKey | null) => {
  return activeStepKey === stepKey;
};

export const getStatusForActiveStep = (activeStep: CreditStepKey): CreditApplicationStatusType => {
  switch (activeStep) {
    case 'companyDetails':
      return CreditApplicationStatusType.STEP1_SAVED;
    case 'billingDetails':
      return CreditApplicationStatusType.STEP2_SAVED;
    case 'insuranceDetails':
      return CreditApplicationStatusType.STEP3_SAVED;
    case 'review':
      return CreditApplicationStatusType.STEP3_SAVED;
    default:
      return CreditApplicationStatusType.STEP1_SAVED;
  }
};

export const getNextButtonLabel = (activeStepKey: CreditStepKey | null) => {
  const index = creditConfig.stepperOrder.findIndex((e) => e === activeStepKey);
  if (index < 0) return;
  const nextStepKey = creditConfig.stepperOrder[index + 1];
  const nextStepLabel = creditConfig.stepperConfig[nextStepKey]?.stepLabel;
  return nextStepLabel ? 'Continue to ' + nextStepLabel : 'Continue';
};

export const getBackButtonLabel = (activeStepKey: CreditStepKey | null) => {
  const index = creditConfig.stepperOrder.findIndex((e) => e === activeStepKey);
  if (index <= 0) return;
  const prevStepKey = creditConfig.stepperOrder[index - 1];
  const prevStepLabel = creditConfig.stepperConfig[prevStepKey]?.backLabel;
  return prevStepLabel ? `Back to ${prevStepLabel}` : 'Back';
};

export const showFormSummary = (stepKey: CreditStepKey, creditState: CreditState) => {
  return creditState.stepper[stepKey]?.isComplete;
};

export const redirectToAccounts = () => {
  removeFlagIfExists('credit_reapply_mode');
  window.location.href = `${environment.ACCOUNT_PATH}`;
};

// Determines if company address needs to be updated
export const shouldUpdateAddress = (
  companyDetails: CompanyFormModel,
  previousCompanyDetails: Partial<CreditFormSubVariant> | undefined,
) => {
  if (!('address' in companyDetails)) return false;

  return (
    !previousCompanyDetails ||
    !('address' in previousCompanyDetails) ||
    companyDetails.address !== previousCompanyDetails.address ||
    companyDetails.city !== previousCompanyDetails.city ||
    companyDetails.state !== previousCompanyDetails.state ||
    companyDetails.zip !== previousCompanyDetails.zip
  );
};

// Gets the billing address state ID
export const getBillingAddressStateId = (
  billingDetails: BillingFormModel,
  states: State[],
): number => {
  const selectedBillingAddressStateName = billingDetails.state;
  const selectedBillingAddressState = states.find(
    (state) => state.name === selectedBillingAddressStateName,
  );
  return selectedBillingAddressState ? selectedBillingAddressState.state_id : states[0].state_id;
};

// Handles errors in components by displaying appropriate error messages
export const handleComponentError = (error: unknown): void => {
  if (error instanceof CustomError) {
    switch (error.code) {
      case ErrorCodes.INVALID_REQUEST:
        openMessageModal(messageContents.invalidRequestErrorMessage);
        break;
      case ErrorCodes.AUTHENTICATION_FAILED:
        openMessageModal(messageContents.authenticationErrorMessage);
        break;
      case ErrorCodes.BLOCKED_REQUEST:
        openMessageModal(messageContents.firewallBlockErrorMessage);
        break;
      case ErrorCodes.CREDIT_LIMIT_ALREADY_SET:
        openMessageModal(messageContents.creditLimitAlreadySet);
        break;
      case ErrorCodes.FORM_ALREADY_SUBMITTED:
        openMessageModal(messageContents.formAlreadySubmittedErrorMessage);
        break;
      case ErrorCodes.SESSION_EXPIRED:
        openMessageModal(messageContents.tokenExpiredErrorMessage);
        break;
      case ErrorCodes.SYSTEM_FAILURE:
        openMessageModal(messageContents.systemFailureErrorMessage);
        break;
      case ErrorCodes.VERIFICATION_FAILED:
        openMessageModal(messageContents.recaptchaValidationErrorMessage);
        break;
      default:
        openMessageModal(messageContents.unexpectedErrorMessage);
    }
  } else {
    // Handle unknown errors
    openMessageModal(messageContents.unexpectedErrorMessage);
  }
};

export const handleGetCreditHelp = () => {
  // open intercom
  openChat();
};

export const formatCreditDate = (dateString?: string | null): string => {
  if (!dateString) {
    return '';
  }

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return formatCreditDate(new Date().toISOString());
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  } catch (error) {
    return formatCreditDate(new Date().toISOString());
  }
};

// Convenience methods that use the getCreditApplicationDetails API
export const getCreditApplicationStatus = (): Promise<CreditApplicationStatusResponse> => {
  return getCreditApplicationDetails('status') as Promise<CreditApplicationStatusResponse>;
};

export const getCreditApplicationInfo = (returnPlain: boolean = false): Promise<CreditApplicationDetails | null> => {
  return getCreditApplicationDetails('details', returnPlain) as Promise<CreditApplicationDetails | null>;
};

export function removeFlagIfExists(key: string) {
  if (localStorage.getItem(key)) {
    localStorage.removeItem(key);
  }
}
